import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Skeleton, SkeletonCard, SkeletonTable } from "../ui/skeleton";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  CleanTable,
  CleanTableHeader,
  CleanTableBody,
  CleanTableHead,
  CleanTableRow,
  CleanTableCell,
  CleanTableExpandableRow,
  CleanTableExpandIcon,
  CleanTableExpandedContent,
  useExpandableTable,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Mail,
  X,
  RefreshCw,
  Calendar,
  User,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { InviteService } from "../../services/inviteService";
import { usePermissions } from "../../hooks/usePermissions";

const InviteManagement = ({
  organizationId,
  businessUnitId,
  businessUnit,
  onInviteUpdate,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isOrgAdmin, isBusinessUnitHead } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [success, setSuccess] = useState(null);

  const { toggleRow, isExpanded } = useExpandableTable();

  const canManageInvites = isOrgAdmin || isBusinessUnitHead;

  const fetchInvites = async () => {
    if (!canManageInvites) return;

    try {
      setLoading(true);
      setError(null);

      let response;
      if (businessUnitId && isBusinessUnitHead && !isOrgAdmin) {
        // Business unit heads can only see their unit's invites
        response = await InviteService.getBusinessUnitInvites(
          businessUnitId,
          {},
          getAccessTokenSilently
        );
      } else if (organizationId && isOrgAdmin) {
        // Org admins can see all organization invites
        response = await InviteService.getOrganizationInvites(
          organizationId,
          businessUnitId ? { business_unit_id: businessUnitId } : {},
          getAccessTokenSilently
        );
      } else {
        return;
      }

      setInvites(response.invites || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && canManageInvites) {
      fetchInvites();
    }
  }, [isOpen, organizationId, businessUnitId, canManageInvites]);

  const handleCancelInvite = async (invite) => {
    try {
      setActionLoading(invite.id);
      setError(null);

      await InviteService.cancelInvite(invite.id, getAccessTokenSilently);

      setSuccess(`Invite to ${invite.email} cancelled successfully`);
      fetchInvites(); // Refresh the list

      if (onInviteUpdate) {
        onInviteUpdate();
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      setActionLoading(invite.id);
      setError(null);

      await InviteService.resendInvite(invite.id, getAccessTokenSilently);

      setSuccess(`Invite to ${invite.email} resent successfully`);
      fetchInvites(); // Refresh the list

      if (onInviteUpdate) {
        onInviteUpdate();
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  const formatRole = (role) => {
    const roleMap = {
      org_admin: "Org Admin",
      business_unit_head: "BU Head",
      business_user: "User",
    };
    return roleMap[role] || role;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  if (!canManageInvites) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setError(null);
          setSuccess(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Manage Invites
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1100px] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manage Invitations</DialogTitle>
          <p className="text-sm text-muted-foreground">
            View and manage pending invitations for{" "}
            {businessUnit ? `"${businessUnit.name}"` : "your organization"}
          </p>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1">
          {error && <Alert variant="destructive">{error}</Alert>}

          {success && (
            <Alert className="border-green-200 bg-green-50 text-green-800">
              <CheckCircle className="h-4 w-4" />
              {success}
            </Alert>
          )}

          {loading ? (
            <div className="space-y-4">
              <SkeletonTable rows={5} columns={7} />
            </div>
          ) : invites.length > 0 ? (
            <TooltipProvider>
              <CleanTable>
                <CleanTableHeader>
                  <CleanTableRow>
                    <CleanTableHead className="w-8"></CleanTableHead>
                    <CleanTableHead>User</CleanTableHead>
                    <CleanTableHead>Role</CleanTableHead>
                    <CleanTableHead>Status</CleanTableHead>
                    <CleanTableHead>Sent</CleanTableHead>
                    <CleanTableHead>Expires</CleanTableHead>
                    {!businessUnitId && (
                      <CleanTableHead>Business Unit</CleanTableHead>
                    )}
                    <CleanTableHead className="w-24">Actions</CleanTableHead>
                  </CleanTableRow>
                </CleanTableHeader>
                <CleanTableBody>
                  {invites.map((invite) => (
                    <React.Fragment key={invite.id}>
                      <CleanTableExpandableRow
                        isExpanded={isExpanded(invite.id)}
                        onToggle={() => toggleRow(invite.id)}
                        className={
                          isExpired(invite.expires_at) ? "opacity-75" : ""
                        }
                      >
                        <CleanTableExpandIcon
                          isExpanded={isExpanded(invite.id)}
                        />
                        <CleanTableCell>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {invite.invited_name || invite.email}
                            </p>
                            {invite.invited_name && (
                              <p className="text-xs text-muted-foreground truncate">
                                {invite.email}
                              </p>
                            )}
                          </div>
                        </CleanTableCell>
                        <CleanTableCell className="text-sm">
                          {formatRole(invite.role)}
                        </CleanTableCell>
                        <CleanTableCell className="text-sm">
                          {isExpired(invite.expires_at) ? (
                            <span className="text-red-600">Expired</span>
                          ) : (
                            <span className="text-muted-foreground">
                              Pending
                            </span>
                          )}
                        </CleanTableCell>
                        <CleanTableCell className="text-sm">
                          {formatDate(invite.created_at)}
                        </CleanTableCell>
                        <CleanTableCell className="text-sm">
                          {formatDate(invite.expires_at)}
                        </CleanTableCell>
                        {!businessUnitId && (
                          <CleanTableCell className="text-sm">
                            {invite.business_unit?.name || "—"}
                          </CleanTableCell>
                        )}
                        <CleanTableCell>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResendInvite(invite);
                                  }}
                                  disabled={actionLoading === invite.id}
                                  className="h-8 w-8 p-0"
                                >
                                  {actionLoading === invite.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Resend invitation</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelInvite(invite);
                                  }}
                                  disabled={actionLoading === invite.id}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:border-red-300"
                                >
                                  {actionLoading === invite.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <X className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Cancel invitation</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </CleanTableCell>
                      </CleanTableExpandableRow>
                      {isExpanded(invite.id) && (
                        <CleanTableExpandedContent
                          colSpan={businessUnitId ? 7 : 8}
                        >
                          <div className="space-y-2 text-sm">
                            {invite.invited_by && (
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  Invited by:
                                </span>
                                <span>{invite.invited_by.name}</span>
                              </div>
                            )}
                            {invite.message && (
                              <div className="flex items-start gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <span className="text-muted-foreground">
                                    Message:
                                  </span>
                                  <p className="italic mt-1">
                                    "{invite.message}"
                                  </p>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Sent:{" "}
                                {new Date(invite.created_at).toLocaleString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Expires:{" "}
                                {new Date(invite.expires_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </CleanTableExpandedContent>
                      )}
                    </React.Fragment>
                  ))}
                </CleanTableBody>
              </CleanTable>
            </TooltipProvider>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No pending invitations found</p>
              <p className="text-sm">
                All invitations have been accepted, expired, or cancelled
              </p>
            </div>
          )}
        </div>

        {invites.length > 0 && (
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {invites.length} pending invitation
              {invites.length !== 1 ? "s" : ""}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchInvites}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteManagement;
